import forOwn from 'lodash/forOwn';
import { moment, IMoment } from '../lib/moment';
import { logd } from '../lib/utils';
const LAYER_ID = 'animationLayer';

export function addOverlay(map) {
  const overlay = new google.maps.OverlayView();
  overlay.onAdd = function () {
    const div = document.createElement('div');
    div.id = LAYER_ID;
    this.getPanes().overlayMouseTarget.appendChild(div);
  };
  overlay.draw = function () {
    this.getPanes().markerLayer.id = 'markerLayer';
  };
  overlay.setMap(map);
  return overlay;
}

function paddedBounds(map, npad, spad, epad, wpad) {
  const SW = map.getBounds().getSouthWest();
  const NE = map.getBounds().getNorthEast();
  const topRight = map.getProjection().fromLatLngToPoint(NE);
  const bottomLeft = map.getProjection().fromLatLngToPoint(SW);
  const scale = Math.pow(2, map.getZoom());

  const SWtopoint = map.getProjection().fromLatLngToPoint(SW);
  const SWpoint = new google.maps.Point(
    ((SWtopoint.x - bottomLeft.x) * scale) + wpad,
    ((SWtopoint.y - topRight.y) * scale) - spad,
  );
  const SWworld = new google.maps.Point(SWpoint.x / scale + bottomLeft.x, SWpoint.y / scale + topRight.y);
  const pt1 = map.getProjection().fromPointToLatLng(SWworld);

  const NEtopoint = map.getProjection().fromLatLngToPoint(NE);
  const NEpoint = new google.maps.Point(
    ((NEtopoint.x - bottomLeft.x) * scale) - epad,
    ((NEtopoint.y - topRight.y) * scale) + npad,
  );
  const NEworld = new google.maps.Point(NEpoint.x / scale + bottomLeft.x, NEpoint.y / scale + topRight.y);
  const pt2 = map.getProjection().fromPointToLatLng(NEworld);

  return new google.maps.LatLngBounds(pt1, pt2);
}

function getMarkerElement(marker) {
  let iterations = 0;
  return new Promise(function checkElement(resolve) {
    let elm = null;
    if (iterations++ % 5 === 0) elm = document.querySelector('img[src="' + marker.icon.url + '"]');
    if (!elm && iterations <= 100) {
      requestAnimationFrame(() => checkElement(resolve));
    } else {
      resolve(elm);
    }
  });
}

function getElementPosition(elm) {
  const regex = /^.*, (\-?\d+(\.\d+)?), (\-?\d+(\.\d+)?)\)$/;
  const matched = getComputedStyle(elm).transform.match(regex);
  return {
    left: parseFloat(matched[1]),
    top: parseFloat(matched[3]),
  };
}

export function attachMarkerAnimation(gmaps, map: google.maps.Map) {
  const overlay = addOverlay(map);

  function fromLatLngToDivPixel(latLng, iconWidth, iconHeight): google.maps.Point {
    const pos = overlay.getProjection().fromLatLngToDivPixel(latLng);
    pos.x = parseInt(`${pos.x}`, 10) - iconWidth;
    pos.y = parseInt(`${pos.y}`, 10) - iconHeight;
    return pos;
  }

  function fromDivPixelToLatLng(pixel: google.maps.Point) {
    return overlay.getProjection().fromDivPixelToLatLng(pixel);
  }

  gmaps.Marker.prototype.animateTo = function (latLng, options) {
    const animationLayer = document.querySelector(`#${LAYER_ID}`);
    const defaultOptions = {
      duration: 1000,
      easing: 'linear', // https://developer.mozilla.org/en-US/docs/Web/CSS/transition-timing-function
      followMode: false,
    };
    options = options || {};

    // complete missing options
    forOwn(defaultOptions, (value, key) => {
      options[key] = options[key] || value;
    });

    const marker: google.maps.Marker = this;
    const easing = options.easing;
    const duration = options.duration;
    const TAB_STATUS = options.TAB_STATUS;

    let resolve;
    const promise = new Promise(res => resolve = res);

    const markerRef = (marker as any);
    const markerIconWidth = markerRef.icon.scaledSize.width / 2;
    const markerIconHeight = markerRef.icon.anchor ? markerRef.icon.anchor.y : markerRef.icon.scaledSize.height;

    let imgElm = null;
    let isAnimationCanceled = false;
    let dragendListener;
    let idleListener;
    let zoomChangedListener;
    let tabInactiveTime: IMoment;
    let tabInactiveDuration: number;

    const endWithoutAnimation = () => {
      marker.setPosition(latLng);
      resolve();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        tabInactiveDuration = 0;
        tabInactiveTime = moment();
      } else {
        const now: IMoment = moment();
        tabInactiveDuration = now.diff(tabInactiveTime);
      }
    };

    const transitionEndHandler = () => {
      logd('tab in active duration', tabInactiveDuration);
      logd('FV: transitionEndHandler before checking imgElm');
      if (!imgElm) return;
      logd('FV: transitionEndHandler after checking imgElm');
      isAnimationCanceled = true;
      google.maps.event.removeListener(dragendListener);
      google.maps.event.removeListener(idleListener);
      google.maps.event.removeListener(zoomChangedListener);
      imgElm.style.transition = 'none';
      imgElm.style.transform = '';
      imgElm.removeEventListener('transitionend', transitionEndHandler);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      marker.setOpacity(1);
      requestAnimationFrame(() => {
        // requestAnimationFrame is used to let the marker opacity(1)
        // get affected before removal of cloned image
        if (animationLayer.contains(imgElm)) animationLayer.removeChild(imgElm);
      });
      if (tabInactiveDuration !== undefined && (tabInactiveDuration > duration)) {
        resolve(TAB_STATUS.ACTIVE_AGAIN_AFTER_ANIMATION_ENDS);
      } else resolve();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange, false);

    const followAnimation = () => {
      const imgElmPosition = getElementPosition(imgElm);
      const markerPosition = fromLatLngToDivPixel(latLng, markerIconWidth, markerIconHeight);
      const currentLatLng = fromDivPixelToLatLng(new google.maps.Point(
        imgElmPosition.left + markerPosition.x,
        imgElmPosition.top + markerPosition.y,
      ));
      const padding = 20;
      const bounds = paddedBounds(map, padding, padding, padding, padding);
      if (!bounds.contains(currentLatLng)) {
        map.panTo(currentLatLng);
      }
    };

    const startFollowMode = () => {
      let start = null;
      requestAnimationFrame(function follow(time) {
        if (!start) start = time;
        const diff = time - start;
        if (isAnimationCanceled) return;
        if (diff > 500) {
          start = time;
          followAnimation();
        }
        requestAnimationFrame(follow);
      });
    };

    getMarkerElement(markerRef).then((elm: HTMLElement) => {
      if (!elm) {
        endWithoutAnimation();
        logd('FV: marker elm not found. setPosition without animation');
        return;
      }
      imgElm = elm.cloneNode() as HTMLElement;
      // this is required to fix visbility issue because
      // from previous animation, it would be 0
      imgElm.style.opacity = '1';
      imgElm.style.cursor = 'pointer';

      if (animationLayer.querySelector(`img[src="${imgElm.src}"]`)) {
        logd('FV: This marker is already animating. Possible duplicate icon issue');
      }

      imgElm.addEventListener('transitionend', transitionEndHandler);
      imgElm.addEventListener('click', () => new google.maps.event.trigger(marker, 'click'));
      elm = null;

      const oldLatLng = marker.getPosition();
      const oldPos = fromLatLngToDivPixel(oldLatLng, markerIconWidth, markerIconHeight);
      const newPos = fromLatLngToDivPixel(latLng, markerIconWidth, markerIconHeight);

      imgElm.style.left = newPos.x + 'px';
      imgElm.style.top = newPos.y + 'px';

      const left = oldPos.x - newPos.x;
      const top = oldPos.y - newPos.y;

      if (left === 0 && top === 0) {
        endWithoutAnimation();
        logd('FV: left, top is zero. setPosition without animation');
        return;
      }

      imgElm.style.transform = `translate(${left}px, ${top}px)`;

      marker.setOpacity(0);
      marker.setPosition(latLng);
      animationLayer.appendChild(imgElm);

      dragendListener = map.addListener('dragend', () => {
        logd('FV: dragend event fired during animation');
        imgElm.style.opacity = '0';
      });

      let animationPercentage = 0;
      let isIdleFired = true;

      idleListener = map.addListener('idle', () => {
        logd('FV: idle event fired during animation');
        requestAnimationFrame(function dragend() {
          if (isAnimationCanceled) return;
          // tslint:disable:no-shadowed-variable
          const oldPos = fromLatLngToDivPixel(oldLatLng, markerIconWidth, markerIconHeight);
          // tslint:disable:no-shadowed-variable
          const newPos = fromLatLngToDivPixel(latLng, markerIconWidth, markerIconHeight);
          imgElm.style.opacity = '1';
          imgElm.style.left = newPos.x + 'px';
          imgElm.style.top = newPos.y + 'px';

          if (animationPercentage) {
            const totalX = (oldPos.x - newPos.x);
            const newComputedLeft = animationPercentage / 100 * totalX;
            const newComputedTop = animationPercentage / 100 * (oldPos.y - newPos.y);
            imgElm.style.transform = `translate(${newComputedLeft}px, ${newComputedTop}px)`;
            requestAnimationFrame(() => {
              if (isAnimationCanceled) return;
              /**
               * When zoom is changed aggresively specially on slow machine,
               * we don't give browser a change to run requestAnimationFrame
               * in idle event which resume animation. In that case we will
               * get transition-property: 'none' and hence currentX and
               * animationPercentage will be zero. So it's important to set
               * isIdleFired here instead on top of this idle event handler.
               */
              isIdleFired = true;
              imgElm.style.transition = `transform ${duration}ms ${easing}`;
              imgElm.style.transform = `translate(0px, 0px)`;
            });
            animationPercentage = 0;
          }
        });
      });

      zoomChangedListener = map.addListener('zoom_changed', () => {
        if (isAnimationCanceled) return;
        if (isIdleFired === false) return;
        logd('FV: zoom_changed event fired during animation');
        isIdleFired = false;
        const { left: currentX } = getElementPosition(imgElm);

        // tslint:disable:no-shadowed-variable
        const oldPos = fromLatLngToDivPixel(oldLatLng, markerIconWidth, markerIconHeight);
        // tslint:disable:no-shadowed-variable
        const newPos = fromLatLngToDivPixel(latLng, markerIconWidth, markerIconHeight);

        animationPercentage = Math.abs(currentX * 100 / (oldPos.x - newPos.x));
        imgElm.style.transition = 'none';
        imgElm.style.opacity = '0';
      });

      requestAnimationFrame(function checkImgElm() {
        if (isAnimationCanceled) return;
        if (animationLayer.contains(imgElm)) {
          requestAnimationFrame(() => {
            if (isAnimationCanceled) return;
            const imgSrc = imgElm.src;
            logd('FV: imgElm found. src=', '...', imgSrc.substring(imgSrc.length - 61, imgSrc.length - 1));
            logd('FV: Starting animation. Duration: ', duration);
            imgElm.style.transition = `transform ${duration}ms ${easing}`;
            imgElm.style.transform = 'translate(0px, 0px)';

            if (options.followMode) startFollowMode();
          });
        } else requestAnimationFrame(checkImgElm);
      });
    });

    return { promise, cancelAnimation: transitionEndHandler };
  };
}
